import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { DatePickerSize, DatePickerState } from "./datePicker.types";
import {
  V2_INPUT_HEIGHT_PX,
  V2_INPUT_WIDTH_PX,
} from "components/designV2/constants";

const COMPACT_INPUT_HEIGHT_PX = 40;
const COMPACT_INPUT_WIDTH_PX = 148;

export const Container = styled.div<{ $size: DatePickerSize }>`
  width: ${(props) =>
    props.$size === "compact"
      ? `${COMPACT_INPUT_WIDTH_PX}px`
      : `${V2_INPUT_WIDTH_PX}px`};
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue4};
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const RequiredMark = styled.span`
  color: ${ColorV2.NegativeRed};
`;

export const FieldWrapper = styled.div<{
  $state: DatePickerState;
  $size: DatePickerSize;
}>`
  display: flex;
  align-items: center;
  height: ${(props) =>
    props.$size === "compact"
      ? `${COMPACT_INPUT_HEIGHT_PX}px`
      : `${V2_INPUT_HEIGHT_PX}px`};
  padding: 0 16px;
  border-radius: ${(props) => (props.$size === "compact" ? "12px" : "16px")};
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease;

  ${(props) =>
    props.$state === "active" &&
    css`
      border-color: ${ColorV2.Blue1};
      box-shadow: 0 6px 16px rgba(15, 42, 64, 0.12);
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      border-color: ${ColorV2.NegativeRed};
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      border-color: ${ColorV2.Blue6};
      background-color: ${ColorV2.Blue7};
    `}
`;

export const Input = styled.input<{
  $state: DatePickerState;
  $size: DatePickerSize;
}>`
  ${TypographyV2.b2}
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0;
  outline: none;
  color: ${ColorV2.Blue1};
  cursor: pointer;

  &::placeholder {
    color: ${ColorV2.Blue5};
  }

  ${(props) =>
    props.$size === "compact" &&
    css`
      ${TypographyV2.s1}
    `}

  ${(props) =>
    props.$state === "inactive" &&
    css`
      color: ${ColorV2.Blue5};
    `}

  ${(props) =>
    props.$state === "error" &&
    css`
      color: ${ColorV2.NegativeRed};

      &::placeholder {
        color: ${ColorV2.NegativeRed};
      }
    `}

  ${(props) =>
    props.$state === "disabled" &&
    css`
      color: ${ColorV2.Blue5};
      cursor: not-allowed;
    `}
`;

export const IconRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
`;

export const IconButton = styled.button<{ $disabled?: boolean }>`
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
`;

export const CalendarIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${ColorV2.Blue5};

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const CalendarPopup = styled.div<{
  $position: { top: number; left: number; width: number } | null;
  $zIndex: number;
  $size: DatePickerSize;
}>`
  position: fixed;
  top: ${(props) => props.$position?.top ?? 0}px;
  left: ${(props) => props.$position?.left ?? 0}px;
  width: ${(props) => props.$position?.width ?? 0}px;
  padding: 16px;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  z-index: ${(props) => props.$zIndex};
`;

export const RadioRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

export const RadioOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

export const RadioInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;

export const RadioControl = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid ${ColorV2.Blue1};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${ColorV2.White};

  ${RadioOption} ${RadioInput}:checked + & {
    border-color: ${ColorV2.Blue1};
  }

  ${RadioOption} ${RadioInput}:checked + &::after {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: ${ColorV2.Blue1};
  }
`;

export const RadioLabel = styled.span`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
`;

export const ToggleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

export const SectionDivider = styled.div`
  height: 1px;
  width: 100%;
  background: ${ColorV2.Blue6};
  margin-bottom: 16px;
`;

export const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

export const NavButton = styled.button`
  ${TypographyV2.b2}
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid ${ColorV2.Blue6};
  background: ${ColorV2.White};
  color: ${ColorV2.Blue1};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const MonthYear = styled.div`
  ${TypographyV2.b2}
  color: ${ColorV2.Blue1};
`;

export const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 4px;
`;

export const DayHeader = styled.div`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  text-align: center;
`;

export const DayCell = styled.button<{
  $isToday: boolean;
  $isSelected: boolean;
  $isOtherMonth: boolean;
  $isExcluded: boolean;
  $disabled: boolean;
}>`
  ${TypographyV2.s1}
  height: 36px;
  border-radius: 10px;
  border: none;
  background: transparent;
  color: ${ColorV2.Blue1};
  cursor: pointer;

  ${(props) =>
    props.$isOtherMonth &&
    css`
      color: ${ColorV2.Blue6};
    `}

  ${(props) =>
    props.$isExcluded &&
    css`
      color: ${ColorV2.NegativeRed};
    `}

  ${(props) =>
    props.$isSelected &&
    css`
      background: ${ColorV2.Blue1};
      color: ${ColorV2.White};
      border: 1px solid ${ColorV2.Blue1};
    `}

  ${(props) =>
    props.$isToday &&
    css`
      border: 1px solid ${ColorV2.Blue3};
    `}

  ${(props) =>
    !props.$disabled &&
    css`
      &:hover {
        background: ${ColorV2.Blue6};
        color: ${ColorV2.Blue1};
      }
    `}

  ${(props) =>
    props.$disabled &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
    `}
`;

export const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`;

export const Message = styled.span<{ $state: DatePickerState }>`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue5};

  ${(props) =>
    props.$state === "error" &&
    css`
      color: ${ColorV2.NegativeRed};
    `}
`;
